import { useRef } from 'react';
import { Anchor, SimpleGrid, Text } from '@mantine/core';

import { useSubscriptions } from '../hooks/useSubscriptions';
import { MantineDataTable, TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';
import { DataTableColumn } from "mantine-datatable";
import { Subscription } from '../types';
import { SubscriptionStatusBadge } from '../profile/SubscriptionStatusBadge';
import { formatTimestamp } from '../../../../utils/TableTimestamps';
import { SubscriptionActions } from '../components/SubscriptionActions';
import { useNavigate } from 'react-router-dom';

interface RecordsTableProps {
    selectedRecords: Subscription[];
    setSelectedRecords: (records: Subscription[]) => void;
}

export const RecordsTable = ({ selectedRecords, setSelectedRecords }: RecordsTableProps) => {
    const {
        items,
        totalCount,
        loading,
        nextToken,
        fetchRecords,
        handleFilterByKeyword
    } = useSubscriptions();

    const navigate = useNavigate();
    const scrollViewportRef = useRef<HTMLDivElement>(null);

    const columns: DataTableColumn<Subscription>[] = [
        {
            accessor: 'name',
            title: 'Name',
            render: (record: Subscription) => {
                const items = record.items?.items || [];

                if (items.length === 0) {
                    return <Text size="sm">Empty subscription</Text>;
                }

                return (
                    <div>
                        {items.slice(0, 3).map((item, index) => (
                            <div key={index}>
                                <Anchor
                                    size="sm"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (item.price?.product?.id) {
                                            navigate(`/products/${item.price.product.id}`);
                                        }
                                    }}
                                    style={{ cursor: 'pointer' }}
                                >
                                    {item.price?.product?.name || `Price ${item.priceId}`}
                                </Anchor>
                                <Text span size="xs" c="dimmed">
                                    {' '}(x{item.quantity}) — ${(item.price?.amount / 100).toFixed(2)} / {item.price?.billingPeriod}
                                </Text>
                            </div>
                        ))}
                        {items.length > 3 && <Text size="xs" c="dimmed">+{items.length - 3} more</Text>}
                    </div>
                );
            }
        },
        {
            accessor: 'status',
            title: 'Status',
            render: (record: Subscription) => <SubscriptionStatusBadge record={record} />
        },
        {
            accessor: 'created',
            title: 'Created',
            render: (record: Subscription) => formatTimestamp(record.created)
        },
        {
            accessor: 'actions',
            title: 'Actions',
            render: (record: Subscription) => <SubscriptionActions subscription={record} onSuccess={fetchRecords} />
        }
    ];

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword} />

            <MantineDataTable
                columns={columns}
                records={items as Subscription[]}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination 
                loadedCount={totalCount} 
                nextToken={nextToken} 
                handleLoadMore={fetchRecords} 
            />
        </SimpleGrid>
    );
};




